import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Box, Typography, CircularProgress, IconButton, Button } from '@mui/material';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CheckCircle, AccessTime, People, TrendingUp, EmojiEvents, ChevronLeft, ChevronRight, Download } from '@mui/icons-material';
import { colors, typography, radius, glassCard } from '../../theme';

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const GOLD = colors.primary, GREEN = colors.success, PURPLE = colors.info, BLUE = '#4a9eff';

// ── helpers ───────────────────────────────────────────────────────────────────
const monthName = (y, m) => new Date(y, m, 1).toLocaleString('default', { month: 'long' });
const isToday   = (d)    => { const t = new Date(); return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth(); };

function getMostRequested(bookings, barbers) {
    const specific = bookings.filter(b => b.barberMode === 'specific' && b.barberId);
    if (!specific.length) return null;
    const counts = specific.reduce((acc, b) => { acc[b.barberId] = (acc[b.barberId] || 0) + 1; return acc; }, {});
    const [topId, topCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return { barber: barbers.find(b => b.id === topId), count: topCount, counts };
}

// ── CSV export ────────────────────────────────────────────────────────────────
function downloadCSV(bookings, shopName, mName, year) {
    const escape = (v) => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = [
        'Date', 'Time', 'Queue No.', 'Customer', 'Phone',
        'Barber', 'Service', 'Type', 'Status',
        'Service Price (RM)', 'Surcharge (RM)', 'Total (RM)', 'Barber Mode',
    ];

    const rows = bookings
        .slice()
        .sort((a, b) => {
            const da = a.scheduledAt?.toDate?.() ?? new Date(0);
            const db_ = b.scheduledAt?.toDate?.() ?? new Date(0);
            return da - db_;
        })
        .map(b => {
            const dt = b.scheduledAt?.toDate?.();
            const date = dt ? dt.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
            const time = dt ? dt.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : '';
            const svcPrice = b.servicePrice ?? b.price ?? 0;
            const surcharge = b.surcharge ?? 0;
            const total = b.totalPrice ?? (svcPrice + surcharge);
            return [
                date,
                time,
                b.queueNumber ?? '',
                b.customerName ?? 'Walk-in',
                b.customerPhone ?? '',
                b.barberName ?? '',
                b.service ?? b.serviceLabel ?? '',
                b.type ?? '',
                b.status ?? '',
                svcPrice,
                surcharge,
                total,
                b.barberMode ?? 'Random',
            ].map(escape).join(',');
        });

    // summary block at the bottom
    const completed  = bookings.filter(b => b.status === 'completed');
    const totalRev   = completed.reduce((s, b) => s + (b.totalPrice ?? (b.servicePrice ?? b.price ?? 0) + (b.surcharge ?? 0)), 0);
    const totalSurch = completed.reduce((s, b) => s + (b.surcharge ?? 0), 0);

    const summary = [
        '',
        `"Summary for ${mName} ${year}"`,
        `"Total Bookings",${bookings.length}`,
        `"Completed",${completed.length}`,
        `"Cancelled",${bookings.filter(b => b.status === 'cancelled').length}`,
        `"Walk-ins",${bookings.filter(b => b.type === 'walkin').length}`,
        `"Appointments",${bookings.filter(b => b.type === 'appointment').length}`,
        `"Total Revenue (RM)",${totalRev.toFixed(2)}`,
        `"Surcharge Revenue (RM)",${totalSurch.toFixed(2)}`,
        `"Exported on",${new Date().toLocaleString('en-MY')}`,
    ];

    const csv  = [headers.join(','), ...rows, ...summary].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `DigitalQ_${shopName ?? 'shop'}_${mName}_${year}.csv`.replace(/\s+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
}

// ── sub-components ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => <Typography key={i} sx={{ fontSize: 12, color: p.color, fontWeight: 600 }}>{p.name}: {p.value}</Typography>)}
        </Box>
    );
};

const StatPill = ({ icon, label, value, color, sub }) => (
    <Box sx={{ flex: 1, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 1.5, textAlign: 'center', ...glassCard }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
        <Typography sx={{ fontFamily: typography.serif, fontSize: 26, fontWeight: 700, color: color ?? colors.textPrimary, lineHeight: 1 }}>{value}</Typography>
        <Typography sx={{ fontSize: 10, color: colors.textFaint, mt: 0.3 }}>{label}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>{sub}</Typography>}
    </Box>
);

const ChartCard = ({ children, title, sub }) => (
    <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 2, ...glassCard }}>
        {title && <>
            <Typography sx={{ fontFamily: typography.serif, fontSize: 15, fontWeight: 700, color: colors.textPrimary, mb: sub ? 0.3 : 1.5 }}>{title}</Typography>
            {sub && <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 1.5 }}>{sub}</Typography>}
        </>}
        {children}
    </Box>
);

const MostRequestedCard = ({ data, barbers, label }) => {
    if (!data?.barber) return null;
    const { barber, count, counts } = data;
    return (
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 1.5, ...glassCard, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: colors.primary }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <EmojiEvents sx={{ fontSize: 20, color: colors.primary }} />
                </Box>
                {barber.image && (
                    <Box component="img" src={barber.image} alt={barber.name}
                        sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${colors.primary}`, flexShrink: 0 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10, color: colors.textFaintest, textTransform: 'uppercase', letterSpacing: 0.5 }}>Most Requested · {label}</Typography>
                    <Typography sx={{ fontFamily: typography.serif, fontSize: 17, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }} noWrap>{barber.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>Chosen specifically {count}×</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: typography.serif, fontSize: 30, fontWeight: 900, color: colors.primary, lineHeight: 1 }}>{count}</Typography>
                    <Typography sx={{ fontSize: 9, color: colors.textFaintest, textTransform: 'uppercase', letterSpacing: 0.5 }}>picks</Typography>
                </Box>
            </Box>
            {Object.keys(counts).length > 1 && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {barbers
                        .filter(b => counts[b.id])
                        .sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0))
                        .reverse()
                        .map(b => {
                            const pct   = Math.round(((counts[b.id] ?? 0) / count) * 100);
                            const isTop = b.id === barber.id;
                            return (
                                <Box key={b.id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                        <Typography sx={{ fontSize: 11, color: isTop ? colors.primary : colors.textMuted, fontWeight: isTop ? 700 : 400 }}>{b.name.split(' ')[0]}</Typography>
                                        <Typography sx={{ fontSize: 11, color: isTop ? colors.primary : colors.textFaint, fontWeight: isTop ? 700 : 400 }}>{counts[b.id]}</Typography>
                                    </Box>
                                    <Box sx={{ bgcolor: colors.border, borderRadius: 1, height: 4, overflow: 'hidden' }}>
                                        <Box sx={{ height: '100%', borderRadius: 1, bgcolor: isTop ? colors.primary : colors.textDisabled, width: `${pct}%`, transition: 'width 1s ease' }} />
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>
            )}
            <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 1 }}>Only counts when customer specifically chose a barber (+RM5)</Typography>
        </Box>
    );
};

// ── Month Picker ──────────────────────────────────────────────────────────────
const MonthPicker = ({ year, month, onChange }) => {
    const now   = new Date();
    const isNow = year === now.getFullYear() && month === now.getMonth();

    const prev = () => { const d = new Date(year, month - 1, 1); onChange(d.getFullYear(), d.getMonth()); };
    const next = () => { if (isNow) return; const d = new Date(year, month + 1, 1); onChange(d.getFullYear(), d.getMonth()); };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, px: 1, py: 0.5, ...glassCard }}>
            <IconButton onClick={prev} size="small" sx={{ color: colors.textMuted, p: 0.5 }}>
                <ChevronLeft sx={{ fontSize: 20 }} />
            </IconButton>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                    {monthName(year, month)}
                </Typography>
                <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>{year}</Typography>
            </Box>
            <IconButton onClick={next} size="small" disabled={isNow} sx={{ color: isNow ? colors.textDisabled : colors.textMuted, p: 0.5 }}>
                <ChevronRight sx={{ fontSize: 20 }} />
            </IconButton>
        </Box>
    );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ShopStats = ({ shopId, shopName, barbers = [] }) => {
    const now = new Date();

    const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
    const [loading,       setLoading]       = useState(true);
    const [bookingsCache, setBookingsCache] = useState({});
    const [range,         setRange]         = useState('week');

    const cacheKey   = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    const monthStart = new Date(selectedYear, selectedMonth, 1);
    const monthEnd   = new Date(selectedYear, selectedMonth + 1, 1);
    const isCurrent  = isToday(new Date(selectedYear, selectedMonth, 1));

    useEffect(() => {
        if (!shopId) return;
        if (bookingsCache[cacheKey]) { setLoading(false); return; }
        const fetchBookings = async () => {
            setLoading(true);
            try {
                const q = query(
                    collection(db, 'bookings'),
                    where('shopId',      '==', shopId),
                    where('scheduledAt', '>=', Timestamp.fromDate(monthStart)),
                    where('scheduledAt', '<',  Timestamp.fromDate(monthEnd)),
                );
                const snap = await getDocs(q);
                setBookingsCache(prev => ({ ...prev, [cacheKey]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchBookings();
    }, [shopId, cacheKey]); // eslint-disable-line

    const allBookings = bookingsCache[cacheKey] ?? [];

    const todayStart    = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayEnd      = new Date(todayStart); todayEnd.setDate(todayEnd.getDate() + 1);
    const todayBookings = isCurrent
        ? allBookings.filter(b => { const d = b.scheduledAt?.toDate?.(); return d >= todayStart && d < todayEnd; })
        : [];

    const totalCompleted = allBookings.filter(b => b.status === 'completed').length;
    const totalWalkins   = allBookings.filter(b => b.type   === 'walkin').length;
    const totalAppts     = allBookings.filter(b => b.type   === 'appointment').length;
    const todayCompleted = todayBookings.filter(b => b.status === 'completed').length;
    const todayWalkins   = todayBookings.filter(b => b.type   === 'walkin').length;
    const todayAppts     = todayBookings.filter(b => b.type   === 'appointment').length;

    const mostRequestedToday = getMostRequested(todayBookings, barbers);
    const mostRequestedMonth = getMostRequested(allBookings,   barbers);

    const last7Days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
        const d    = new Date(todayStart); d.setDate(d.getDate() - (6 - i));
        const dEnd = new Date(d); dEnd.setDate(dEnd.getDate() + 1);
        const dayB = allBookings.filter(b => { const bd = b.scheduledAt?.toDate?.(); return bd >= d && bd < dEnd; });
        return { day: DAY_LABELS[d.getDay()], walkins: dayB.filter(b => b.type === 'walkin').length, appts: dayB.filter(b => b.type === 'appointment').length, done: dayB.filter(b => b.status === 'completed').length };
    }), [allBookings]); // eslint-disable-line

    const weeksInMonth = useMemo(() => {
        const weeks = []; const cursor = new Date(monthStart); let w = 1;
        const end = isCurrent ? todayEnd : monthEnd;
        while (cursor < end) {
            const wStart = new Date(cursor);
            const wEnd   = new Date(cursor); wEnd.setDate(wEnd.getDate() + 7);
            const wb = allBookings.filter(b => { const bd = b.scheduledAt?.toDate?.(); return bd >= wStart && bd < wEnd; });
            weeks.push({ week: `W${w++}`, walkins: wb.filter(b => b.type === 'walkin').length, appts: wb.filter(b => b.type === 'appointment').length, done: wb.filter(b => b.status === 'completed').length });
            cursor.setDate(cursor.getDate() + 7);
        }
        return weeks;
    }, [allBookings]); // eslint-disable-line

    const statusData = [
        { name: 'Completed', value: allBookings.filter(b => b.status === 'completed').length, color: colors.success },
        { name: 'Cancelled', value: allBookings.filter(b => b.status === 'cancelled').length, color: colors.error },
        { name: 'Waiting',   value: allBookings.filter(b => b.status === 'waiting').length,   color: colors.info },
        { name: 'Serving',   value: allBookings.filter(b => b.status === 'serving').length,   color: colors.primary },
    ].filter(d => d.value > 0);

    const typeData = [
        { name: 'Walk-ins',     value: totalWalkins, color: BLUE },
        { name: 'Appointments', value: totalAppts,   color: GOLD },
    ].filter(d => d.value > 0);

    const barberData = barbers.map(barber => {
        const bb = allBookings.filter(b => b.barberId === barber.id);
        return { name: barber.name.split(' ')[0], total: bb.length, done: bb.filter(b => b.status === 'completed').length };
    }).filter(b => b.total > 0);

    const hourlyData = Array.from({ length: 10 }, (_, i) => {
        const hour = 8 + i; const h = hour % 12 === 0 ? 12 : hour % 12;
        const hB = todayBookings.filter(b => Math.floor((b.slotMinutes ?? 0) / 60) === hour);
        return { time: `${h}${hour < 12 ? 'AM' : 'PM'}`, walkins: hB.filter(b => b.type === 'walkin').length, appts: hB.filter(b => b.type === 'appointment').length };
    });

    const chartData = range === 'week' ? last7Days : weeksInMonth;
    const chartKey  = range === 'week' ? 'day' : 'week';
    const mName     = monthName(selectedYear, selectedMonth);

    return (
        <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>

            {/* ── Header row with title + download button ─────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>Statistics</Typography>
                {!loading && allBookings.length > 0 && (
                    <Button
                        size="small"
                        startIcon={<Download sx={{ fontSize: 15 }} />}
                        onClick={() => downloadCSV(allBookings, shopName, mName, selectedYear)}
                        sx={{
                            fontSize: 12, fontWeight: 600, textTransform: 'none',
                            color: colors.textPrimary,
                            bgcolor: colors.primary,
                            border: `1px solid ${colors.primary}`,
                            borderRadius: radius.md,
                            px: 1.5, py: 0.5,
                            '&:hover': { bgcolor: colors.primaryDim, opacity: 0.85 },
                        }}
                    >
                        Export CSV
                    </Button>
                )}
            </Box>

            {/* ── Month Picker ─────────────────────────────────────────────── */}
            <MonthPicker
                year={selectedYear}
                month={selectedMonth}
                onChange={(y, m) => { setSelectedYear(y); setSelectedMonth(m); setLoading(true); }}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : (
                <>
                    {/* ── TODAY ────────────────────────────────────────────── */}
                    {isCurrent && (
                        <>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>Today</Typography>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <StatPill icon={<People      sx={{ fontSize: 18, color: colors.primary }} />} label="Bookings"     value={todayBookings.length} color={colors.primary} />
                                    <StatPill icon={<CheckCircle sx={{ fontSize: 18, color: PURPLE }} />}         label="Completed"    value={todayCompleted}       color={PURPLE} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                                    <StatPill icon={<TrendingUp  sx={{ fontSize: 18, color: GREEN }} />}          label="Walk-ins"     value={todayWalkins}         color={GREEN} />
                                    <StatPill icon={<AccessTime  sx={{ fontSize: 18, color: BLUE }} />}           label="Appointments" value={todayAppts}            color={BLUE} />
                                </Box>
                            </Box>
                            <MostRequestedCard data={mostRequestedToday} barbers={barbers} label="Today" />
                            <ChartCard title="Today — Hourly Breakdown" sub="Walk-ins and appointments by hour">
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={hourlyData} barGap={2} barCategoryGap="25%">
                                        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                                        <XAxis dataKey="time" tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                                        <Bar dataKey="walkins" name="Walk-ins"     fill={colors.primary} radius={[3,3,0,0]} />
                                        <Bar dataKey="appts"   name="Appointments" fill={colors.info}    radius={[3,3,0,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </>
                    )}

                    {/* ── SELECTED MONTH ───────────────────────────────────── */}
                    <Box>
                        <Typography sx={{ fontSize: 11, color: colors.textFaintest, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>{mName} {selectedYear}</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <StatPill icon={<People      sx={{ fontSize: 18, color: colors.primary }} />} label="Bookings"     value={allBookings.length} color={colors.primary} />
                            <StatPill icon={<CheckCircle sx={{ fontSize: 18, color: PURPLE }} />}         label="Completed"    value={totalCompleted}     color={PURPLE} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                            <StatPill icon={<TrendingUp  sx={{ fontSize: 18, color: GREEN }} />}          label="Walk-ins"     value={totalWalkins}       color={GREEN} />
                            <StatPill icon={<AccessTime  sx={{ fontSize: 18, color: BLUE }} />}           label="Appointments" value={totalAppts}          color={BLUE} />
                        </Box>
                    </Box>

                    <MostRequestedCard data={mostRequestedMonth} barbers={barbers} label={mName} />

                    {isCurrent && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {['week', 'month'].map(r => (
                                <Box key={r} onClick={() => setRange(r)} sx={{ px: 2, py: 0.6, borderRadius: radius.md, cursor: 'pointer', bgcolor: range === r ? colors.primary : colors.bgCard, border: `1px solid ${range === r ? colors.primary : colors.border}`, transition: 'all .2s' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: range === r ? colors.bg : colors.textMuted }}>
                                        {r === 'week' ? 'Last 7 Days' : mName}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <ChartCard title={isCurrent && range === 'week' ? 'Bookings — Last 7 Days' : `Bookings — ${mName} ${selectedYear}`}>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={isCurrent ? chartData : weeksInMonth} barGap={2} barCategoryGap="30%">
                                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                                <XAxis dataKey={isCurrent ? chartKey : 'week'} tick={{ fill: colors.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: colors.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                                <Bar dataKey="walkins" name="Walk-ins"     fill={colors.primary} radius={[3,3,0,0]} />
                                <Bar dataKey="appts"   name="Appointments" fill={colors.info}    radius={[3,3,0,0]} />
                                <Bar dataKey="done"    name="Completed"    fill={colors.success} radius={[3,3,0,0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
                            {[['Walk-ins', colors.primary], ['Appointments', colors.info], ['Completed', colors.success]].map(([l, c]) => (
                                <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
                                    <Typography sx={{ fontSize: 10, color: colors.textFaint }}>{l}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </ChartCard>

                    {statusData.length > 0 && (
                        <ChartCard title="Booking Status Breakdown" sub={`All bookings in ${mName} ${selectedYear}`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ResponsiveContainer width="50%" height={160}>
                                    <PieChart>
                                        <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                                            {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {statusData.map(d => (
                                        <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color }} />
                                            <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{d.name}</Typography>
                                            <Typography sx={{ fontSize: 12, color: d.color, fontWeight: 700, ml: 'auto' }}>{d.value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </ChartCard>
                    )}

                    {typeData.length > 0 && (
                        <ChartCard title="Walk-ins vs Appointments">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ResponsiveContainer width="50%" height={140}>
                                    <PieChart>
                                        <Pie data={typeData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={4} dataKey="value">
                                            {typeData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {typeData.map(d => (
                                        <Box key={d.name}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color }} />
                                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{d.name}</Typography>
                                            </Box>
                                            <Typography sx={{ fontFamily: typography.serif, fontSize: 22, fontWeight: 700, color: d.color, ml: 2.5 }}>{d.value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </ChartCard>
                    )}

                    {barberData.length > 0 && (
                        <ChartCard title="Barber Performance" sub={`Total and completed bookings in ${mName} ${selectedYear}`}>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={barberData} barGap={4} barCategoryGap="35%">
                                    <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                                    <XAxis dataKey="name" tick={{ fill: colors.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                                    <Bar dataKey="total" name="Total"     fill={colors.info}    radius={[3,3,0,0]} />
                                    <Bar dataKey="done"  name="Completed" fill={colors.success} radius={[3,3,0,0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}

                    {allBookings.length > 0 && (
                        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 2, ...glassCard }}>
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1 }}>Completion Rate · {mName} {selectedYear}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontFamily: typography.serif, fontSize: 40, fontWeight: 700, color: GREEN }}>
                                    {Math.round((totalCompleted / allBookings.length) * 100)}%
                                </Typography>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: colors.textMuted }}>{totalCompleted} completed</Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textFaintest }}>of {allBookings.length} total bookings</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1.5, bgcolor: colors.border, borderRadius: 1, height: 6, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', borderRadius: 1, bgcolor: GREEN, width: `${Math.round((totalCompleted / allBookings.length) * 100)}%`, transition: 'width 1s ease' }} />
                            </Box>
                        </Box>
                    )}

                    {/* ── Bottom CSV export (repeat for convenience) ───────── */}
                    {allBookings.length > 0 && (
                        <Button
                            fullWidth
                            startIcon={<Download sx={{ fontSize: 16 }} />}
                            onClick={() => downloadCSV(allBookings, shopName, mName, selectedYear)}
                            sx={{
                                fontSize: 13, fontWeight: 600, textTransform: 'none',
                                color: colors.textPrimary,
                                bgcolor: colors.primary,
                                border: `1px solid ${colors.primaryBorder}`,
                                borderRadius: radius.lg,
                                py: 1.2,
                                '&:hover': { bgcolor: colors.primaryDim, opacity: 0.85 },
                            }}
                        >
                            Download {mName} {selectedYear} as CSV
                        </Button>
                    )}

                    {allBookings.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography sx={{ fontSize: 32, mb: 1 }}>📭</Typography>
                            <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: colors.textMuted }}>No bookings in {mName} {selectedYear}</Typography>
                            <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>Try a different month</Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ShopStats;